/**
 * Performance monitoring utility for tracking and improving app performance
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {};
    this.operations = new Map();
  }

  /**
   * Start timing an operation
   * @param {string} label - Operation name
   * @param {string} category - Category for grouping operations
   */
  start(label, category = 'default') {
    const key = `${category}:${label}`;
    this.operations.set(key, {
      start: performance.now(),
      label,
      category
    });
  }

  /**
   * End timing an operation and record metrics
   * @param {string} label - Operation name
   * @param {string} category - Category for grouping operations
   * @returns {number|null} - Duration in ms or null if operation not found
   */
  end(label, category = 'default') {
    const key = `${category}:${label}`;
    const operation = this.operations.get(key);
    
    if (!operation) return null;
    
    const duration = performance.now() - operation.start;
    this.operations.delete(key);
    
    // Initialize category in metrics if not exists
    if (!this.metrics[category]) {
      this.metrics[category] = {};
    }
    
    // Record the metric
    this.metrics[category][label] = {
      duration,
      timestamp: Date.now(),
      ...(this.metrics[category][label] || {}),
      count: ((this.metrics[category][label]?.count || 0) + 1)
    };
    
    return duration;
  }

  /**
   * Measure a function's execution time
   * @param {Function} fn - Function to measure
   * @param {string} label - Operation name
   * @param {string} category - Category for grouping operations
   * @returns {any} - Result of the function
   */
  measure(fn, label, category = 'default') {
    this.start(label, category);
    try {
      const result = fn();
      
      // Handle promises
      if (result instanceof Promise) {
        return result.finally(() => this.end(label, category));
      }
      
      this.end(label, category);
      return result;
    } catch (error) {
      this.end(label, category);
      throw error;
    }
  }

  /**
   * Get all recorded metrics
   * @returns {Object} - Metrics data
   */
  getMetrics() {
    return this.metrics;
  }

  /**
   * Log summary of performance metrics to console
   * @param {string} category - Category to log (optional, logs all if not specified)
   */
  logSummary(category) {
    if (category) {
      if (this.metrics[category]) {
        console.group(`Performance Metrics - ${category}`);
        Object.entries(this.metrics[category]).forEach(([label, data]) => {
          console.log(`${label}: ${data.duration.toFixed(2)}ms (called ${data.count} times)`);
        });
        console.groupEnd();
      } else {
        console.log(`No metrics for category: ${category}`);
      }
      return;
    }
    
    // Log all categories
    console.group('Performance Metrics Summary');
    Object.entries(this.metrics).forEach(([category, operations]) => {
      console.group(`Category: ${category}`);
      Object.entries(operations).forEach(([label, data]) => {
        console.log(`${label}: ${data.duration.toFixed(2)}ms (called ${data.count} times)`);
      });
      console.groupEnd();
    });
    console.groupEnd();
  }
  
  /**
   * Clear all metrics
   */
  clear() {
    this.metrics = {};
    this.operations.clear();
  }
  
  /**
   * Start tracking performance across the application
   */
  startTracking() {
    console.log('Performance monitoring started');
    this.isTracking = true;
    this.trackingStartTime = performance.now();
    
    // Initialize tracking interval if needed
    if (typeof window !== 'undefined') {
      this.trackingInterval = setInterval(() => {
        this.logSummary();
      }, 60000); // Log summary every minute
    }
  }
  
  /**
   * Stop tracking performance
   */
  stopTracking() {
    if (this.trackingInterval) {
      clearInterval(this.trackingInterval);
    }
    
    console.log('Performance monitoring stopped');
    this.isTracking = false;
    const totalDuration = performance.now() - this.trackingStartTime;
    console.log(`Total tracking duration: ${(totalDuration / 1000).toFixed(2)} seconds`);
  }
  
  /**
   * Create a decorator to measure function execution time
   * @param {string} label - Operation name
   * @param {string} category - Category for grouping operations
   * @returns {Function} - Decorator function
   */
  createDecorator(label, category = 'default') {
    return (target, propertyKey, descriptor) => {
      const originalMethod = descriptor.value;
      
      descriptor.value = function(...args) {
        return performanceMonitor.measure(
          () => originalMethod.apply(this, args),
          label || propertyKey,
          category
        );
      };
      
      return descriptor;
    };
  }
  
  /**
   * Track a custom metric value
   * @param {string} name - Metric name
   * @param {number} value - Metric value
   * @param {string} category - Category for grouping
   */
  trackMetric(name, value, category = 'custom') {
    if (!this.metrics[category]) {
      this.metrics[category] = {};
    }
    
    this.metrics[category][name] = {
      value,
      timestamp: Date.now()
    };
  }
  
  /**
   * Run a benchmark test multiple times and average the results
   * @param {string} name - Benchmark name
   * @param {Function} fn - Function to benchmark
   * @param {number} iterations - Number of iterations (default: 3)
   * @param {string} category - Category for grouping
   * @returns {Object} - Benchmark results
   */
  benchmark(name, fn, iterations = 3, category = 'benchmarks') {
    console.log(`Running benchmark: ${name} (${iterations} iterations)`);
    
    const times = [];
    let result;
    
    for (let i = 0; i < iterations; i++) {
      const start = performance.now();
      result = fn();
      const end = performance.now();
      times.push(end - start);
    }
    
    const total = times.reduce((sum, time) => sum + time, 0);
    const average = total / iterations;
    
    if (!this.metrics[category]) {
      this.metrics[category] = {};
    }
    
    this.metrics[category][name] = {
      average,
      min: Math.min(...times),
      max: Math.max(...times),
      iterations,
      timestamp: Date.now()
    };
    
    console.log(`Benchmark ${name}: Average ${average.toFixed(2)}ms over ${iterations} iterations`);
    return result;
  }
}

export const performanceMonitor = new PerformanceMonitor();

// Re-export the existing job performance monitor for backward compatibility
export { jobPerformance } from './jobPerformance';